<template>
  <div class="cn-contact-form">
    <div v-if="formPending" class="cn-contact-form__pending-overlay">
      <UiSpinner />
    </div>
    <div v-if="formSuccess" class="cn-contact-form__success">
      <p>{{ $t('success_form_request') }}</p>
    </div>
    <div v-else>
      <form class="cn-contact-form__form" @submit.prevent="submitForm">
        <div class="cn-contact-form__form-grid">
          <div class="cn-contact-form__name">
            <UiInput
              v-model.trim="form.name"
              :placeholder="$t('full_name')"
              :error="errorMessage(v$.name)"
              @update:model-value="v$.name.$touch()"
            />
          </div>

          <div class="cn-contact-form__email">
            <UiInput
              v-model.trim="form.email"
              :placeholder="$t('email')"
              :error="errorMessage(v$.email)"
              @update:model-value="v$.email.$touch()"
            />
          </div>

          <div class="cn-contact-form__country">
            <UiInput
              v-model="form.country"
              :placeholder="$t('country')"
              :error="errorMessage(v$.country)"
              @update:model-value="v$.country.$touch()"
            />
          </div>

          <div class="cn-contact-form__product-type">
            <UiSelect
              v-model.trim="form.message_subject"
              :placeholder="$t('message_subject')"
              :error="errorMessage(v$.message_subject)"
              :options="messageSubjects"
              @update:model-value="v$.message_subject.$touch()"
            />
          </div>

          <div class="cn-contact-form__comment">
            <UiTextarea
              v-model.trim="form.comment"
              :placeholder="$t('message')"
              :error="errorMessage(v$.comment)"
              @update:model-value="v$.comment.$touch()"
            />
          </div>

          <UiRecaptcha
            class="cn-contact-form__recaptcha"
            @error="captchaWidget.setToken('')"
            @success="captchaWidget.setToken($event)"
            @expired="captchaWidget.setToken('')"
          />

          <div class="cn-contact-form__form-controls">
            <UiButton
              variant="red"
              type="submit"
              :disabled="isSubmitDisabled(v$.$invalid)"
            >
              {{ $t('send') }}
            </UiButton>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';

import { useForm, useValidationRules } from '@/composables';
import UiTextarea from '@/components/ui/UiTextarea';
import UiSpinner from '@/components/ui/UiSpinner';
import UiSelect from '@/components/ui/UiSelect';
import UiInput from '@/components/ui/UiInput';
import UiButton from '@/components/ui/UiButton';
import UiRecaptcha from '@/components/ui/UiRecaptcha';

const { contactFormUuid } = useRuntimeConfig().public.frontkit;
const { t } = useI18n();
const {
  formSuccess,
  formPending,
  submit,
  recaptcha: captchaWidget,
  isSubmitDisabled,
} = useForm({ confirm: false });
const { required, email, errorMessage } = useValidationRules();

const form = ref({
  name: '',
  email: '',
  country: '',
  message_subject: null,
  comment: '',
});

const rules = {
  name: { required },
  email: { required, email },
  country: { required },
  message_subject: { required },
  comment: { required },
};
const v$ = useVuelidate(rules, form);

const submitForm = () => {
  submit(form.value, contactFormUuid);
};

const messageSubjects = [
  {
    id: 1,
    name: t('website_content'),
  },
  {
    id: 2,
    name: t('sales_inquily'),
  },
  {
    id: 3,
    name: t('press_relation'),
  },
  {
    id: 4,
    name: t('other'),
  },
];
</script>

<style lang="scss">
.cn-contact-form {
  --ab-canyon-contact-form-pending-overlay-color-bg: var(
    --ab-canyon-color-light
  );
  position: relative;

  &__pending-overlay {
    background-color: color-mix(
      in srgb,
      var(--ab-canyon-contact-form-pending-overlay-color-bg),
      transparent 50%
    );
    padding-top: calc(4 * var(--size-base));
    padding-bottom: calc(4 * var(--size-base));
    justify-content: center;
    display: flex;
    z-index: 10;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    position: absolute;
  }
  &__success {
    p {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
  }
  &__form-grid {
    gap: var(--size-xl);
    grid-template-columns: repeat(1, minmax(0, 1fr));
    display: grid;
    @media (min-width: 656px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  &__name {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
  &__email {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
  &__country {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
  &__product-type {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
  &__comment {
    grid-column: span 2 / span 2;
  }
  &__recaptcha {
    grid-column: span 2 / span 2;
  }
  &__form-controls {
    grid-column: span 2 / span 2;
    @media (min-width: 656px) {
      grid-column: span 1 / span 1;
    }
  }
}
</style>
